import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/css';
import JourneyCard from '../components/JourneyCard';
import { useSprintWebSocket, useWebSocketSubscription, useNavigationResult } from '../context/SprintWebSocketContext';
import { ReadyState } from 'react-use-websocket';
import { JourneyOption, WebSocketEventType, JourneyOptionsMessage } from '../../types/sprintLessonTypes';

const JourneySelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { sendJsonMessage, readyState, isConnected } = useSprintWebSocket();
  const [journeyOptions, setJourneyOptions] = useState<JourneyOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userProfile, setUserProfile] = useState('');
  
  // Subscribe to journey options messages directly
  useWebSocketSubscription(
    (message) => message.event_type === WebSocketEventType.JOURNEY_OPTIONS,
    (message: JourneyOptionsMessage) => {
      console.log('Journey options received:', message);
      setJourneyOptions(message.journey_options);
      setIsLoading(false);
    }
  );
  
  // Subscribe to navigation updates
  useNavigationResult((result) => {
    console.log('Navigation update received:', result);
    
    if (result.view === 'lesson_view' && result.journey_id && result.lesson_id) {
      // Navigate to the lesson page with the journey_id and lesson_id
      // Use a relative path within the SharpSprintRouter context
      navigate(`journey/${result.journey_id}/lesson/${result.lesson_id}`);
    }
  });

  // We no longer automatically request journey options when connected
  // Instead, we'll wait for the user to click the button

  const containerStyle = css`
    max-width: 1200px;
    margin: 0 auto;
    padding: 1rem;
  `;
  
  const formContainerStyle = css`
    margin-bottom: 2rem;
    display: flex;
    flex-direction: column;
  `;

  const textareaStyle = css`
    width: 100%;
    min-height: 150px;
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    margin-bottom: 1rem;
    font-family: inherit;
    resize: vertical;
  `;

  const buttonStyle = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.625rem 1rem;
    font-weight: 500;
    border-radius: 0.375rem;
    background-color: #3b82f6;
    color: white;
    cursor: pointer;
    transition: background-color 0.2s;
    border: none;
    max-width: fit-content;
    margin-right: 0.5rem;

    &:hover {
      background-color: #2563eb;
    }

    &:disabled {
      background-color: #9ca3af;
      cursor: not-allowed;
    }
  `;

  const secondaryButtonStyle = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.625rem 1rem;
    font-weight: 500;
    border-radius: 0.375rem;
    background-color: #6b7280;
    color: white;
    cursor: pointer;
    transition: background-color 0.2s;
    border: none;
    max-width: fit-content;
    margin-right: 0.5rem;

    &:hover {
      background-color: #4b5563;
    }

    &:disabled {
      background-color: #9ca3af;
      cursor: not-allowed;
    }
  `;

  const buttonContainerStyle = css`
    display: flex;
    flex-direction: row;
    margin-bottom: 1rem;
  `;

  const handleGenerateJourneys = () => {
    if (readyState === ReadyState.OPEN) {
      setIsLoading(true);
      sendJsonMessage({
        type: "create_journey_options",
        user_profile: userProfile
      });
    }
  };

  const handleSaveJourneyOptions = () => {
    if (journeyOptions.length > 0) {
      // Save both journey options and user profile
      localStorage.setItem('savedJourneyOptions', JSON.stringify(journeyOptions));
      localStorage.setItem('savedUserProfile', userProfile);
      // alert('Journey options saved successfully!');
    } else {
      alert('No journey options to save');
    }
  };

  const handleLoadJourneyOptions = () => {
    const savedOptions = localStorage.getItem('savedJourneyOptions');
    const savedProfile = localStorage.getItem('savedUserProfile');
    
    if (savedOptions) {
      try {
        const parsedOptions = JSON.parse(savedOptions);
        setJourneyOptions(parsedOptions);
        
        // Also restore the user profile if available
        if (savedProfile) {
          setUserProfile(savedProfile);
        }
        // alert('Journey options loaded successfully!');
      } catch (e) {
        alert('Error loading saved journey options');
        console.error('Error parsing saved journey options:', e);
      }
    } else {
      alert('No saved journey options found');
    }
  };

  const titleStyle = css`
    font-size: 1.5rem;
    font-weight: bold;
    margin-bottom: 1.5rem;
  `;

  const loadingContainerStyle = css`
    display: flex;
    justify-content: center;
    margin-top: 2rem;
    margin-bottom: 2rem;
  `;

  const gridStyle = css`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;
    @media (min-width: 768px) {
      grid-template-columns: repeat(3, 1fr);
    }
  `;

  // Connection status indicators
  const connectionStatusStyle = css`
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    margin-bottom: 1rem;
  `;

  const statusIndicatorStyle = (connected: boolean) => css`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: ${connected ? '#10b981' : '#ef4444'};
    margin-right: 0.5rem;
  `;

  const getConnectionStatus = () => {
    if (readyState === ReadyState.CONNECTING) return 'Connecting...';
    if (readyState === ReadyState.OPEN) return 'Connected';
    if (readyState === ReadyState.CLOSING) return 'Closing...';
    if (readyState === ReadyState.CLOSED) return 'Disconnected';
    return 'Uninstantiated';
  };

  return (
    <div className={containerStyle}>
      <h1 className={titleStyle}>Sharp Sprint</h1>
      
      <div className={connectionStatusStyle}>
        <div className={statusIndicatorStyle(isConnected)} />
        <span>{getConnectionStatus()}</span>
      </div>

      <div className={formContainerStyle}>
        <h2>Tell us about yourself</h2>
        <p>Enter details about your role, stage/expertise, goals, obstacles and any other relevant information to get personalized journey recommendations:</p>
        <textarea
          className={textareaStyle}
          value={userProfile}
          onChange={(e) => setUserProfile(e.target.value)}
          placeholder="Example: I'm a new startup founder, looking for product market fit, recently launched a closed beta small MVP but haven't seen much traction yet."
        />
        <div className={buttonContainerStyle}>
          <button
            className={buttonStyle}
            onClick={handleGenerateJourneys}
            disabled={!isConnected || userProfile.trim() === '' || isLoading}
          >
            Generate Journey Options
          </button>
          <button
            className={secondaryButtonStyle}
            onClick={handleSaveJourneyOptions}
            disabled={journeyOptions.length === 0 || isLoading}
          >
            Save Options
          </button>
          <button
            className={secondaryButtonStyle}
            onClick={handleLoadJourneyOptions}
            disabled={isLoading}
          >
            Load Saved Options
          </button>
        </div>
      </div>
      
      {isLoading && (
        <div className={loadingContainerStyle}>
          <p>Loading journey options...</p>
        </div>
      )}
      
      {!isLoading && (
        <div className={gridStyle}>
          {journeyOptions.map((option) => (
            <JourneyCard
              key={option.journey_id}
              journey={option}
              onClick={(journeyId, journeyOption) => {
                setIsLoading(true);
                sendJsonMessage({
                  type: "select_journey",
                  journey_id: journeyId,
                  journey_option: journeyOption,
                  user_profile: userProfile
                });
              }}
            />
          ))}
        </div>
      )}
      
      {!isLoading && journeyOptions.length === 0 && (
        <p>No journey options available yet. Try refreshing the page.</p>
      )}
    </div>
  );
};

export default JourneySelectionPage;
